import {
  type ILedgerLine,
  type LedgerSection,
  ReportingBucket,
} from '../types/buckets';
import type {
  ICashFlowFacts,
  IComparedFacts,
  IFactTable,
  ILineMover,
  IMonthFacts,
  IMonthlyPoint,
  IPeriodFacts,
} from '../types/metrics';
import type { IResolvedPeriod, MonthKey } from '../types/period';
import {
  BALANCE_SHEET_BUCKETS,
  BucketHelper,
  PNL_BUCKETS,
} from './BucketHelper';
import { FactTableHelper } from './FactTableHelper';
import { MonthKeyHelper } from './MonthKeyHelper';

type CashFlowAmountKey = {
  [K in keyof ICashFlowFacts]: ICashFlowFacts[K] extends number | null
    ? K
    : never;
}[keyof ICashFlowFacts];

const EXPENSE_SECTIONS: LedgerSection[] = [
  'cost_of_goods_sold',
  'operating_expenses',
  'non_operating_expenses',
];

/** Aggregates month facts over period windows and compares them. */
export class MetricsHelper {
  /** Flows are summed across the window; balances are taken at the last present month. */
  static aggregate(table: IFactTable, months: MonthKey[]): IPeriodFacts {
    const rows = MetricsHelper.rows(table, months);
    const last = rows[rows.length - 1];
    const buckets = BucketHelper.emptyTotals();
    const lines: Record<string, number> = {};

    rows.forEach((row) => {
      PNL_BUCKETS.forEach((b) => {
        buckets[b] += row.buckets[b];
      });
      Object.entries(row.lines).forEach(([id, v]) => {
        if (table.lineById.get(id)?.statement === 'PNL')
          lines[id] = (lines[id] ?? 0) + v;
      });
    });
    if (last) {
      BALANCE_SHEET_BUCKETS.forEach((b) => {
        buckets[b] = last.buckets[b];
      });
      buckets[ReportingBucket.EXCLUDED] =
        last.buckets[ReportingBucket.EXCLUDED];
      Object.entries(last.lines).forEach(([id, v]) => {
        if (table.lineById.get(id)?.statement === 'BS') lines[id] = v;
      });
    }

    const days = rows.reduce((s, r) => s + r.days, 0);
    const pnl = FactTableHelper.pnlFromBuckets(buckets);
    const balance = FactTableHelper.balanceFromBuckets(buckets);
    const burn = last ? MetricsHelper.trailing3Burn(table, last.month) : null;
    return {
      months,
      presentMonths: rows.map((r) => r.month),
      complete: rows.length === months.length && rows.length > 0,
      days,
      buckets,
      lines,
      pnl,
      balance,
      cashFlow: MetricsHelper.sumCashFlows(rows),
      workingCapital: FactTableHelper.workingCapitalDays(
        pnl,
        balance,
        days || 30
      ),
      runwayMonths: MetricsHelper.runwayFromBurn(
        balance.cashAndInvestments,
        burn
      ),
      trailing3Burn: burn,
    };
  }

  static compare(table: IFactTable, resolved: IResolvedPeriod): IComparedFacts {
    const current = MetricsHelper.aggregate(table, resolved.months);
    const windows = resolved.comparisonWindows.map((w) =>
      MetricsHelper.aggregate(table, w)
    );
    const first = windows[0];
    const comparison =
      resolved.comparison === 'trailing_3_avg'
        ? MetricsHelper.meanOf(windows)
        : first && first.presentMonths.length > 0
          ? first
          : null;
    return { current, comparison };
  }

  /** Average monthly net burn over the three months ending at `endMonth`. */
  static trailing3Burn(table: IFactTable, endMonth: MonthKey): number | null {
    const rows = MetricsHelper.rows(table, [
      endMonth,
      MonthKeyHelper.add(endMonth, -1),
      MonthKeyHelper.add(endMonth, -2),
    ]).filter((r) => r.cashFlow.hasOpening);
    if (rows.length === 0) return null;
    return rows.reduce((s, r) => s + r.cashFlow.netBurn, 0) / rows.length;
  }

  static runwayFromBurn(
    cashAndInvestments: number,
    burn: number | null
  ): number | null {
    if (burn === null || burn <= 0) return null;
    return cashAndInvestments / burn;
  }

  static pctChange(
    current: number,
    prior: number | null | undefined
  ): number | null {
    if (prior === null || prior === undefined || prior === 0) return null;
    return (current - prior) / Math.abs(prior);
  }

  /** Expense lines (COGS + opex + non-operating expense) ranked by current-period spend. */
  static expenseLines(
    table: IFactTable,
    facts: IPeriodFacts,
    comparison: IPeriodFacts | null
  ): ILineMover[] {
    return MetricsHelper.movers(table, facts, comparison, {
      include: (line) => EXPENSE_SECTIONS.includes(line.section),
      keep: (mover) => mover.current !== 0 || mover.prior !== 0,
      pct: (mover) =>
        MetricsHelper.pctChange(mover.current, comparison ? mover.prior : null),
    }).sort((a, b) => b.current - a.current);
  }

  /** All P&L lines ranked by absolute movement versus the comparison window. */
  static lineMovers(
    table: IFactTable,
    facts: IPeriodFacts,
    comparison: IPeriodFacts | null
  ): ILineMover[] {
    return MetricsHelper.movers(table, facts, comparison, {
      include: () => true,
      keep: (mover) => mover.delta !== 0,
      pct: (mover) => MetricsHelper.pctChange(mover.current, mover.prior),
    }).sort((a, b) => Math.abs(b.delta) - Math.abs(a.delta));
  }

  /** Per-month series for trend components; months without closed data yield null. */
  static monthlySeries<T>(
    table: IFactTable,
    months: MonthKey[],
    pick: (facts: IMonthFacts) => T
  ): IMonthlyPoint<T>[] {
    return months.map((month) => {
      const row = table.byMonth.get(month);
      return { month, value: row ? pick(row) : null };
    });
  }

  static movingAverage(
    values: (number | null)[],
    window: number
  ): (number | null)[] {
    return values.map((_, i) => {
      const slice = values
        .slice(Math.max(0, i - window + 1), i + 1)
        .filter((v): v is number => v !== null);
      if (slice.length < window) return null;
      return slice.reduce((s, v) => s + v, 0) / slice.length;
    });
  }

  private static rows(table: IFactTable, months: MonthKey[]): IMonthFacts[] {
    return months
      .map((m) => table.byMonth.get(m))
      .filter((r): r is IMonthFacts => r !== undefined);
  }

  private static movers(
    table: IFactTable,
    facts: IPeriodFacts,
    comparison: IPeriodFacts | null,
    rules: {
      include: (line: ILedgerLine) => boolean;
      keep: (mover: ILineMover) => boolean;
      pct: (mover: ILineMover) => number | null;
    }
  ): ILineMover[] {
    return table.lines
      .filter((line) => line.statement === 'PNL' && rules.include(line))
      .map((line) => {
        const bucket = table.bucketMap.get(line.id) ?? ReportingBucket.EXCLUDED;
        const current = facts.lines[line.id] ?? 0;
        const prior = comparison?.lines[line.id] ?? 0;
        const mover: ILineMover = {
          line,
          bucket,
          current,
          prior,
          delta: current - prior,
          pct: null,
          pctOfRevenue: facts.pnl.revenue ? current / facts.pnl.revenue : null,
        };
        return mover;
      })
      .filter(
        (mover) =>
          mover.bucket !== ReportingBucket.EXCLUDED && rules.keep(mover)
      )
      .map((mover) => ({ ...mover, pct: rules.pct(mover) }));
  }

  private static sumAmount(
    rows: ICashFlowFacts[],
    key: CashFlowAmountKey
  ): number {
    return rows.reduce((s, r) => s + (r[key] ?? 0), 0);
  }

  private static sumCashFlows(rows: IMonthFacts[]): ICashFlowFacts {
    const first = rows[0];
    const last = rows[rows.length - 1];
    const flows = rows
      .filter((r) => r.cashFlow.hasOpening)
      .map((r) => r.cashFlow);
    const add = (key: CashFlowAmountKey) => MetricsHelper.sumAmount(flows, key);
    const cfsAvailable =
      flows.length > 0 && flows.every((f) => f.cfsVariance !== null);
    return {
      hasOpening: flows.length > 0,
      openingCash: first?.cashFlow.openingCash ?? 0,
      netIncome: add('netIncome'),
      depreciationAmortisation: add('depreciationAmortisation'),
      changeInReceivables: add('changeInReceivables'),
      changeInInventory: add('changeInInventory'),
      changeInOtherCurrentAssets: add('changeInOtherCurrentAssets'),
      changeInPayables: add('changeInPayables'),
      changeInOtherLiabilities: add('changeInOtherLiabilities'),
      workingCapitalChange: add('workingCapitalChange'),
      operating: add('operating'),
      capex: add('capex'),
      investmentsPurchased: add('investmentsPurchased'),
      otherInvesting: add('otherInvesting'),
      investing: add('investing'),
      debtRaised: add('debtRaised'),
      equityRaised: add('equityRaised'),
      otherFinancing: add('otherFinancing'),
      financing: add('financing'),
      unexplained: add('unexplained'),
      netChange: add('netChange'),
      closingCash: last?.cashFlow.closingCash ?? 0,
      netBurn: add('netBurn'),
      cfsVariance: cfsAvailable ? add('cfsVariance') : null,
    };
  }

  /** Element-wise mean of the windows that have data; used for the trailing-3 comparison. */
  private static meanOf(windows: IPeriodFacts[]): IPeriodFacts | null {
    const present = windows.filter((w) => w.presentMonths.length > 0);
    const base = present[0];
    if (!base) return null;
    const n = present.length;
    const buckets = BucketHelper.emptyTotals();
    const lines: Record<string, number> = {};
    present.forEach((w) => {
      Object.values(ReportingBucket).forEach((b) => {
        buckets[b] += w.buckets[b] / n;
      });
      Object.entries(w.lines).forEach(([id, v]) => {
        lines[id] = (lines[id] ?? 0) + v / n;
      });
    });
    const pnl = FactTableHelper.pnlFromBuckets(buckets);
    const balance = FactTableHelper.balanceFromBuckets(buckets);
    const days = present.reduce((s, w) => s + w.days, 0) / n;
    const flows = present.map((w) => w.cashFlow);
    const avg = (key: CashFlowAmountKey) =>
      MetricsHelper.sumAmount(flows, key) / n;
    return {
      ...base,
      months: present.flatMap((w) => w.months),
      presentMonths: present.flatMap((w) => w.presentMonths),
      complete: present.every((w) => w.complete),
      days,
      buckets,
      lines,
      pnl,
      balance,
      cashFlow: {
        ...base.cashFlow,
        operating: avg('operating'),
        investing: avg('investing'),
        financing: avg('financing'),
        netBurn: avg('netBurn'),
        netChange: avg('netChange'),
      },
      workingCapital: FactTableHelper.workingCapitalDays(
        pnl,
        balance,
        days || 30
      ),
      runwayMonths: present.reduce((s, w) => s + (w.runwayMonths ?? 0), 0) / n,
      trailing3Burn:
        present.reduce((s, w) => s + (w.trailing3Burn ?? 0), 0) / n,
    };
  }
}
